import { useEffect, useMemo, useState } from 'react';
import {
  defaultSiteDesignSettings,
  loadSiteDesignSettings,
  normalizeSiteDesignSettings,
  resetLocalSiteDesignSettings,
  saveSiteDesignSettings,
  type HomeTextPosition,
  type SiteDesignSettings,
} from '../../../lib/siteSettings';

const positionLabels: Record<HomeTextPosition, string> = {
  underLogo: 'تحت اللوكو مباشرة',
  underAds: 'تحت الإعلان',
  aboveIcons: 'فوق الأيقونات',
  belowIcons: 'تحت الأيقونات',
};

export default function DesignPanel() {
  const [settings, setSettings] = useState<SiteDesignSettings>(defaultSiteDesignSettings);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const sortedItems = useMemo(() => [...settings.menuItems].sort((a, b) => a.order - b.order), [settings.menuItems]);

  useEffect(() => {
    loadSiteDesignSettings().then((result) => {
      setSettings(result.settings);
      setLoading(false);
    });
  }, []);

  function updateItem(id: string, patch: Record<string, unknown>) {
    setSettings((current) => ({ ...current, menuItems: current.menuItems.map((item) => item.id === id ? { ...item, ...patch } : item) }));
  }

  function moveItem(id: string, direction: -1 | 1) {
    const list = [...sortedItems];
    const index = list.findIndex((item) => item.id === id);
    const next = index + direction;
    if (index < 0 || next < 0 || next >= list.length) return;
    [list[index], list[next]] = [list[next], list[index]];
    const orderMap = new Map(list.map((item, order) => [item.id, order]));
    setSettings((current) => ({ ...current, menuItems: current.menuItems.map((item) => ({ ...item, order: orderMap.get(item.id) ?? item.order })) }));
  }

  async function save() {
    setMessage('جاري الحفظ...');
    const result = await saveSiteDesignSettings(settings);
    setSettings(result.settings);
    setMessage(result.remote ? 'تم حفظ التصميم للموقع كله ✅' : 'تم حفظه للمعاينة على هذا الجهاز. فعّل جدول إعدادات التصميم من ملف SQL حتى يصير الحفظ لجميع الزوار.');
  }

  function reset() {
    if (!window.confirm('ترجع تصميم الصفحة الرئيسية للوضع الافتراضي؟')) return;
    const defaults = resetLocalSiteDesignSettings();
    setSettings(defaults);
    setMessage('رجعنا الإعدادات للوضع الافتراضي. اضغط حفظ حتى تثبتها.');
  }

  function addTextBlock() {
    setSettings((current) => ({
      ...current,
      textBlocks: [...current.textBlocks, { id: crypto.randomUUID(), text: 'اكتب النص هنا', position: 'aboveIcons', visible: true }],
    }));
  }

  if (loading) return <div className="rounded-3xl border border-white/10 bg-white/5 p-8 text-center text-gray-400">جاري تحميل التصميم...</div>;

  return (
    <div className="space-y-6">
      <section className="rounded-3xl border border-white/10 bg-white/5 p-5 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6"><div><h2 className="font-bold text-xl">التصميم والواجهة</h2><p className="text-sm text-gray-400 mt-1">غير الأحجام والترتيب بدون ما تتدخل بروابط أو محتوى الصفحات الداخلية.</p></div><div className="flex gap-2"><button onClick={reset} className="rounded-xl border border-white/10 px-4 py-2 text-sm">رجوع افتراضي</button><button onClick={save} className="rounded-xl bg-[#F5A623] text-black font-bold px-5 py-2">حفظ التصميم</button></div></div>
        {message && <div className="mb-5 rounded-2xl bg-[#F5A623]/10 border border-[#F5A623]/20 px-4 py-3 text-sm text-[#F5A623]">{message}</div>}

        <div className="grid md:grid-cols-2 gap-5">
          <div className="rounded-2xl bg-black/20 border border-white/10 p-5">
            <h3 className="font-bold mb-4">اللوكو</h3>
            <label className="text-sm text-gray-300 flex justify-between mb-2"><span>حجم اللوكو</span><span>{settings.logoSize}px</span></label>
            <input type="range" min="220" max="520" step="10" value={settings.logoSize} onChange={(e) => setSettings({ ...settings, logoSize: Number(e.target.value) })} className="w-full accent-[#F5A623]" />
            <label className="text-sm text-gray-300 block mt-5 mb-2">الكتابة تحت اللوكو</label>
            <input value={settings.headingText} onChange={(e) => setSettings({ ...settings, headingText: e.target.value })} className="admin-input" />
          </div>

          <div className="rounded-2xl bg-black/20 border border-white/10 p-5">
            <h3 className="font-bold mb-4">حجم الأيقونات والكروت</h3>
            <label className="text-sm text-gray-300 flex justify-between mb-2"><span>حجم الأيقونة</span><span>{settings.iconSize}px</span></label>
            <input type="range" min="56" max="112" step="4" value={settings.iconSize} onChange={(e) => setSettings({ ...settings, iconSize: Number(e.target.value) })} className="w-full accent-[#F5A623]" />
            <label className="text-sm text-gray-300 flex justify-between mt-5 mb-2"><span>حجم الكارت من الخارج</span><span>{settings.cardPadding}px</span></label>
            <input type="range" min="18" max="48" step="2" value={settings.cardPadding} onChange={(e) => setSettings({ ...settings, cardPadding: Number(e.target.value) })} className="w-full accent-[#F5A623]" />
            <label className="text-sm text-gray-300 flex justify-between mt-5 mb-2"><span>المسافة بين الأيقونات</span><span>{settings.gridGap}px</span></label>
            <input type="range" min="8" max="32" step="2" value={settings.gridGap} onChange={(e) => setSettings({ ...settings, gridGap: Number(e.target.value) })} className="w-full accent-[#F5A623]" />
          </div>
        </div>
      </section>

      <section className="rounded-3xl border border-white/10 bg-white/5 p-5 sm:p-6">
        <div className="mb-5"><h2 className="font-bold text-lg">الأيقونات</h2><p className="text-xs text-gray-400 mt-1">غير الاسم والترتيب أو اخفِ الأيقونة. الرابط والصورة الداخلية ما نلمسها.</p></div>
        <div className="space-y-3">
          {sortedItems.map((item, index) => (
            <div key={item.id} className="rounded-2xl border border-white/10 bg-black/20 p-4 flex flex-col md:flex-row md:items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-[#F5A623]/15 text-[#F5A623] grid place-items-center shrink-0"><i className={`${item.icon} text-2xl`} /></div>
              <input value={item.label} onChange={(e) => updateItem(item.id, { label: e.target.value })} className="admin-input flex-1" />
              <label className="flex items-center gap-2 text-sm whitespace-nowrap"><input type="checkbox" checked={item.visible} onChange={(e) => updateItem(item.id, { visible: e.target.checked })} className="accent-[#F5A623] w-5 h-5" /> ظاهر</label>
              <div className="flex gap-2"><button type="button" disabled={index === 0} onClick={() => moveItem(item.id, -1)} className="rounded-lg border border-white/10 px-3 py-2 disabled:opacity-30" title="تحريك للأعلى">↑</button><button type="button" disabled={index === sortedItems.length - 1} onClick={() => moveItem(item.id, 1)} className="rounded-lg border border-white/10 px-3 py-2 disabled:opacity-30" title="تحريك للأسفل">↓</button></div>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-3xl border border-white/10 bg-white/5 p-5 sm:p-6">
        <div className="flex items-center justify-between gap-3 mb-5"><div><h2 className="font-bold text-lg">نصوص الصفحة الرئيسية</h2><p className="text-xs text-gray-400 mt-1">تضيف كتابات فقط بالصفحة الرئيسية وتختار مكانها.</p></div><button onClick={addTextBlock} className="rounded-xl bg-white/10 px-4 py-2 text-sm">+ إضافة نص</button></div>
        {settings.textBlocks.length === 0 ? <div className="rounded-2xl border border-dashed border-white/10 p-8 text-center text-gray-500">ماكو نصوص إضافية حالياً.</div> : <div className="space-y-3">{settings.textBlocks.map((block) => (
          <div key={block.id} className="rounded-2xl border border-white/10 bg-black/20 p-4 grid md:grid-cols-[1fr_220px_auto] gap-3 items-start">
            <textarea value={block.text} onChange={(e) => setSettings((current) => ({ ...current, textBlocks: current.textBlocks.map((item) => item.id === block.id ? { ...item, text: e.target.value } : item) }))} className="admin-input min-h-24" />
            <div className="space-y-3"><select value={block.position} onChange={(e) => setSettings((current) => ({ ...current, textBlocks: current.textBlocks.map((item) => item.id === block.id ? { ...item, position: e.target.value as HomeTextPosition } : item) }))} className="admin-input">{Object.entries(positionLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={block.visible} onChange={(e) => setSettings((current) => ({ ...current, textBlocks: current.textBlocks.map((item) => item.id === block.id ? { ...item, visible: e.target.checked } : item) }))} className="accent-[#F5A623] w-5 h-5" /> إظهار النص</label></div>
            <button onClick={() => setSettings((current) => ({ ...current, textBlocks: current.textBlocks.filter((item) => item.id !== block.id) }))} className="rounded-xl bg-red-500/10 text-red-300 px-4 py-3">حذف</button>
          </div>
        ))}</div>}
      </section>

      <section className="rounded-3xl border border-[#F5A623]/20 bg-[#F5A623]/5 p-5">
        <h3 className="font-bold text-[#F5A623]">معاينة سريعة</h3>
        <p className="text-xs text-gray-400 mt-1 mb-4">هاي بس حتى تشوف النسب. المعاينة الحقيقية من زر «عرض الموقع».</p>
        <div className="rounded-2xl bg-[#1b1b1b] border border-white/10 p-5 text-center overflow-hidden">
          <img src="https://static.readdy.ai/image/645339dfad8e1015d6f60a0aacde7112/7ac018fe95b5893e2914eabc8075ecee.png" alt="Logo" className="mx-auto object-contain" style={{ width: Math.min(220, settings.logoSize * 0.5), height: Math.min(220, settings.logoSize * 0.5) }} />
          <p className="font-bold -mt-2">{settings.headingText}</p>
          <div className="mt-5 flex justify-center gap-3 flex-wrap">{sortedItems.filter((item) => item.visible).slice(0, 5).map((item) => <div key={item.id} className="rounded-xl bg-white/5 border border-white/10 px-3 py-3 min-w-28"><div className="mx-auto rounded-xl bg-[#F5A623] grid place-items-center text-white" style={{ width: settings.iconSize * 0.62, height: settings.iconSize * 0.62 }}><i className={`${item.icon} text-xl`} /></div><p className="text-[11px] mt-2">{item.label}</p></div>)}</div>
        </div>
      </section>
    </div>
  );
}
